import {
  Controller,
  DatePicker,
  FormProvider,
  Input,
  Stack,
  useForm,
} from '@qred/components-library';
import dayjs from 'dayjs';
import React, { useContext, useEffect } from 'react';
import { FieldPath } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { ValidationContext } from '~/components/hoc/withValidation';
import { FormErrorBanner } from '~/components/shared/FormErrorBanner/FormErrorBanner';
import { NLAddressLookup } from '~/components/shared/NLAddressLookup/NLAddressLookup';
import NextButton from '~/components/shared/Stepper/components/NextButton';
import StepHeader from '~/components/shared/Stepper/components/StepHeader';
import { removeWhitespace } from '~/helpers/formatters.helper';
import { buildStreetValue } from '~/helpers/shared.helper';
import useTranslate from '~/hooks/useTranslate';
import { ILoanApplicationApplicant } from '~/interfaces/loanApplication';
import { RootState } from '~/store';
import { pushToGtmLoanAction } from '~/store/actions/gtmActions';
import { updateApplicantData } from '~/store/slices/loanApplication.slice';
import { ApiStatus } from '~/store/types/sharedTypes';
import {
  StandardStepContent,
  StandardStepContentBody,
  StandardStepContentFooter,
} from '~/styles/BaseStyledComponents';
import useGoToNextStep from '../Steps/useGoToNextStep';
import { AboutYouForm, AboutYouSchema } from './AboutYou.schema';

const DEFAULT_DATE_OF_BIRTH = new Date(
  new Date().setFullYear(new Date().getFullYear() - 18)
);

/**
 * Step B of the 3-step basic flow (behind the
 * AB_Web_LoanApplication_ContactDetailsEarlier_MoreRecoverableLeads_NL flag,
 * currently tested in NL only): personal details + address, following
 * contact details on step A.
 */
export const AboutYou = () => {
  const t = useTranslate();
  const {
    form: {
      applicant: { firstName, lastName, dateOfBirth, addressFields },
    },
    apiStatus: applicationApiStatus,
  } = useSelector((state: RootState) => state.loanApplication);

  const onNextStepClick = useGoToNextStep();

  const formMethods = useForm<AboutYouForm>({
    schema: AboutYouSchema,
    defaultValues: {
      firstName,
      lastName,
      dateOfBirth: dateOfBirth ? new Date(dateOfBirth) : undefined,
      address: addressFields,
    },
    mode: 'onTouched',
  });

  const {
    watch,
    register,
    setValue,
    control,
    formState: { errors, isValid, isValidating },
  } = formMethods;

  const fieldValues = watch();

  const dispatch = useDispatch();
  const validationContext = useContext(ValidationContext);

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const trimmedValue = value.trim();
    setValue(name as FieldPath<AboutYouForm>, trimmedValue);
    dispatch(
      pushToGtmLoanAction({
        actionName: `${name}_change`,
      })
    );
  };

  useEffect(() => {
    if (isValid && !isValidating) {
      const updatedForm: Partial<ILoanApplicationApplicant> = {
        firstName: fieldValues.firstName,
        lastName: fieldValues.lastName,
        dateOfBirth: dayjs(fieldValues.dateOfBirth).format('YYYY-MM-DD'),
        addressFields: {
          zipCode: fieldValues.address.zipCode,
          houseNumber: fieldValues.address.houseNumber,
          houseNumberAddition: fieldValues.address.houseNumberAddition,
          city: fieldValues.address.city,
          streetName: fieldValues.address.streetName,
        },
        address: {
          city: fieldValues.address.city,
          street: buildStreetValue({ ...fieldValues.address }),
          zipCode: removeWhitespace(fieldValues.address.zipCode),
        },
      };

      dispatch(updateApplicantData(updatedForm));
      validationContext.removePropertyFromValidationErrors('aboutYouStep');
    } else {
      validationContext.addPropertyToValidationErrors('aboutYouStep');
    }

    return () => {
      validationContext.removePropertyFromValidationErrors('aboutYouStep');
    };
  }, [isValid, isValidating, dispatch]);

  return (
    <StandardStepContent>
      <StandardStepContentBody>
        <StepHeader
          title="Onboarding.AboutYouTitle"
          description="Onboarding.AboutYouDescription"
        />
        <FormProvider {...formMethods}>
          <Stack spacing={'xs'}>
            <Input.Group orientation="vertical">
              <Input
                {...register('firstName', { onBlur })}
                label={t('FirstName')}
                error={
                  errors?.firstName?.message && t(errors?.firstName?.message)
                }
                id="firstName"
              />
              <Input
                {...register('lastName', { onBlur })}
                label={t('LastName')}
                error={
                  errors?.lastName?.message && t(errors?.lastName?.message)
                }
                id="lastName"
              />
              <Controller
                name="dateOfBirth"
                control={control}
                render={({ field, fieldState }) => (
                  <DatePicker
                    label={t('DateOfBirth')}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={() => {
                      field.onBlur();
                      dispatch(
                        pushToGtmLoanAction({
                          actionName: 'dateOfBirth_change',
                        })
                      );
                    }}
                    error={
                      fieldState.error?.message && t(fieldState.error?.message)
                    }
                    valueFormat="DD-MM-YYYY"
                    defaultDate={DEFAULT_DATE_OF_BIRTH}
                    maxDate={new Date()}
                    id="dateOfBirth"
                  />
                )}
              />
              <NLAddressLookup />
            </Input.Group>
            <FormErrorBanner errors={errors} />
          </Stack>
        </FormProvider>
      </StandardStepContentBody>
      <StandardStepContentFooter>
        <NextButton
          label="Onboarding.Next"
          isLoading={applicationApiStatus.patch === ApiStatus.Started}
          action={onNextStepClick}
        />
      </StandardStepContentFooter>
    </StandardStepContent>
  );
};
